/**
 * @file     rentVsBuyMath.ts
 * @purpose  Pure financial model for the Pro rent vs buy tool: amortization, year-by-year buyer/renter net worth, and break-even detection.
 */
import { amortizedPayment, canadianPeriodicRate } from '@/core/finance/mortgage';

export type RentVsBuyInputs = {
  // Buy side
  homePrice: number;
  downPayment: number;
  mortgageRatePct: number;
  amortizationYears: number;
  propertyTaxAnnual: number;
  strataMonthly: number;
  appreciationPct: number;
  closingCosts: number;
  sellingCostsPct: number;
  // Rent side
  rentMonthly: number;
  rentGrowthPct: number;
  rentersInsuranceMonthly: number;
  // Shared
  investmentReturnPct: number;
  marginalTaxPct: number;
  horizonYears: number;
};

export type YearPoint = {
  year: number;
  buyerNetWorth: number;
  renterNetWorth: number;
  buyerCumCost: number;
  renterCumCost: number;
  homeValue: number;
  mortgageBalance: number;
  rentMonthly: number;
};

export type RentVsBuyResult = {
  series: YearPoint[];
  breakEvenYear: number | null;
  buyerNetWorthAtHorizon: number;
  renterNetWorthAtHorizon: number;
  buyerCumCostAtHorizon: number;
  renterCumCostAtHorizon: number;
  buyerWins: boolean;
};

/** Monthly mortgage payment (principal + interest) using standard amortization. */
export function monthlyMortgagePayment(principal: number, annualRatePct: number, years: number): number {
  return amortizedPayment(principal, annualRatePct / 100, years * 12);
}

/** Run the year-by-year simulation and return the full series plus headline metrics. */
export function computeRentVsBuy(inputs: RentVsBuyInputs): RentVsBuyResult {
  const {
    homePrice,
    downPayment,
    mortgageRatePct,
    amortizationYears,
    propertyTaxAnnual,
    strataMonthly,
    appreciationPct,
    closingCosts,
    sellingCostsPct,
    rentMonthly: initialRent,
    rentGrowthPct,
    rentersInsuranceMonthly,
    investmentReturnPct,
    marginalTaxPct,
    horizonYears,
  } = inputs;

  const principal = Math.max(homePrice - downPayment, 0);
  const monthlyPI = monthlyMortgagePayment(principal, mortgageRatePct, amortizationYears);
  const monthlyRate = canadianPeriodicRate(mortgageRatePct / 100, 12);

  // After-tax investment return: applies to the renter's invested cash and the monthly differential.
  const afterTaxReturn = (investmentReturnPct / 100) * (1 - marginalTaxPct / 100);
  const annualAppreciation = appreciationPct / 100;
  const annualRentGrowth = rentGrowthPct / 100;

  // Year 0 starting state.
  // Buyer paid down payment + closing costs out of pocket. Cumulative cost so far: closing costs (down payment is equity, not cost).
  // Renter starts with the same upfront cash invested at after-tax return.
  let mortgageBalance = principal;
  let homeValue = homePrice;
  let buyerCumCost = closingCosts; // closing costs are sunk; down payment becomes equity
  let renterCumCost = 0;
  // Renter's investment portfolio: down payment + closing costs at t=0 (the cash buyer used).
  let renterPortfolio = downPayment + closingCosts;
  let currentRentAnnual = initialRent * 12;
  const insuranceAnnual = rentersInsuranceMonthly * 12;

  const series: YearPoint[] = [];

  for (let year = 1; year <= horizonYears; year += 1) {
    // BUY side: 12 months of mortgage payments amortizing down balance.
    let yearInterestAndPrincipal = 0;
    for (let m = 0; m < 12; m += 1) {
      if (mortgageBalance <= 0) {
        // Mortgage paid off: only carrying costs remain.
        yearInterestAndPrincipal += 0;
        continue;
      }
      const interest = mortgageBalance * monthlyRate;
      const principalPaid = Math.min(monthlyPI - interest, mortgageBalance);
      mortgageBalance = Math.max(mortgageBalance - principalPaid, 0);
      yearInterestAndPrincipal += monthlyPI;
    }
    const yearPropertyTax = propertyTaxAnnual;
    const yearStrata = strataMonthly * 12;
    const yearBuyerOutOfPocket = yearInterestAndPrincipal + yearPropertyTax + yearStrata;
    buyerCumCost += yearBuyerOutOfPocket;

    // Home appreciates.
    homeValue = homeValue * (1 + annualAppreciation);

    // RENT side: 12 months of rent + insurance.
    const yearRent = currentRentAnnual;
    const yearRenterOutOfPocket = yearRent + insuranceAnnual;
    renterCumCost += yearRenterOutOfPocket;

    // Monthly differential: if buyer is paying more than renter, renter "saves" the difference and invests it.
    // If buyer pays less, the renter has nothing extra to invest (we don't make the renter borrow).
    const monthlyDiff = (yearBuyerOutOfPocket - yearRenterOutOfPocket) / 12;
    // Grow existing portfolio for the year (compound annually for simplicity).
    renterPortfolio = renterPortfolio * (1 + afterTaxReturn);
    if (monthlyDiff > 0) {
      // Approximate mid-year contributions: 12 monthly contributions, each earning ~half a year of growth.
      const annualContribution = monthlyDiff * 12;
      renterPortfolio += annualContribution * (1 + afterTaxReturn / 2);
    }

    // Rent grows for next year.
    currentRentAnnual = currentRentAnnual * (1 + annualRentGrowth);

    // BUYER net worth: home equity (value minus mortgage) minus selling costs if they sold today.
    const sellingCosts = homeValue * (sellingCostsPct / 100);
    const buyerNetWorth = homeValue - mortgageBalance - sellingCosts;

    // RENTER net worth: their investment portfolio.
    const renterNetWorth = renterPortfolio;

    series.push({
      year,
      buyerNetWorth,
      renterNetWorth,
      buyerCumCost,
      renterCumCost,
      homeValue,
      mortgageBalance,
      rentMonthly: yearRent / 12,
    });
  }

  // Break-even: first year buyerNetWorth >= renterNetWorth.
  const breakEvenPoint = series.find((p) => p.buyerNetWorth >= p.renterNetWorth);
  const breakEvenYear = breakEvenPoint ? breakEvenPoint.year : null;

  const last = series[series.length - 1];
  return {
    series,
    breakEvenYear,
    buyerNetWorthAtHorizon: last?.buyerNetWorth ?? 0,
    renterNetWorthAtHorizon: last?.renterNetWorth ?? 0,
    buyerCumCostAtHorizon: last?.buyerCumCost ?? 0,
    renterCumCostAtHorizon: last?.renterCumCost ?? 0,
    buyerWins: breakEvenYear !== null,
  };
}

/** Sensible defaults per the tool spec. */
export const defaultRentVsBuyInputs = (): RentVsBuyInputs => {
  const homePrice = 1_200_000;
  return {
    homePrice,
    downPayment: 240_000,
    mortgageRatePct: 5.0,
    amortizationYears: 25,
    // Greater Vancouver mill rate average ~0.30%.
    propertyTaxAnnual: Math.round(homePrice * 0.003),
    strataMonthly: 0,
    appreciationPct: 3,
    // BC PTT (~1% on first $200k + 2% above) plus legal + inspection
    // averages ~2.75% of price for typical Greater Vancouver purchases.
    closingCosts: Math.round(homePrice * 0.0275),
    sellingCostsPct: 5,
    rentMonthly: 3500,
    rentGrowthPct: 3,
    rentersInsuranceMonthly: 25,
    investmentReturnPct: 5,
    marginalTaxPct: 30,
    horizonYears: 10,
  };
};
